import classNames from 'classnames'
import { FC, PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { NavigationLeftIcon } from '@icons/NavigationLeftIcon'
import { NavigationRightIcon } from '@icons/NavigationRightIcon'

import { Tooltip } from '@ui/Popups/Tooltip/Tooltip'

import { useWindowResize } from '@utils/domUtils'

import { HorizontalScroll, HorizontalScrollFade } from '@global/HorizontalScroll/HorizontalScroll'
import IconButton from '@global/iconButton/IconButton'

import { HorizontalScroll as ScrollProps } from '@hooks/useHorizontalScroll'

import styles from './FilterHorizontalScroll.module.scss'

const FADE_WIDTH = '2.5rem'
const SCROLL_RANGE = 250

export const FilterHorizontalScroll: FC<PropsWithChildren> = (props) => {
  const { children } = props
  const isArabic = useArabicSessionLanguage()

  const [isMouseOver, setIsMouseOver] = useState(false)

  const isMobile = useWindowResize()
  const { t } = useTranslation()

  const onNavigateLeft = (scrollProps: ScrollProps) => {
    scrollProps.scrollContainerRef?.current?.scrollBy({
      left: -SCROLL_RANGE,
      behavior: 'smooth',
    })
  }

  const onNavigateRight = (scrollProps: ScrollProps) => {
    scrollProps.scrollContainerRef?.current?.scrollBy({
      left: SCROLL_RANGE,
      behavior: 'smooth',
    })
  }

  return (
    <HorizontalScroll
      className={styles.filterHorizontalScroll}
      containerClassName={styles.badgesContainer}
      innerClassName={styles.badgesScroll}
      fadeWidth={FADE_WIDTH}
      onMouseOver={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
      shouldRestorePosition
      renderAdditionalContent={(scrollProps) => {
        const { isScrollable, hasScrolledToStart, hasScrolledToEnd } = scrollProps

        const hasScrolledEnd = isArabic ? hasScrolledToStart : hasScrolledToEnd
        const hasScrolledStart = isArabic ? hasScrolledToEnd : hasScrolledToStart

        if (!isScrollable || isMobile) {
          return null
        }

        return (
          <>
            <div
              className={classNames(styles.navigationButton, styles.left, {
                [styles.visible]: isMouseOver && !hasScrolledStart,
              })}
            >
              <Tooltip.New content={t('Move Left')}>
                <IconButton.New
                  onClick={() => onNavigateLeft(scrollProps)}
                  appearance='secondary'
                  size='M'
                >
                  <NavigationLeftIcon />
                </IconButton.New>
              </Tooltip.New>
              <div className={styles.fade}>
                <HorizontalScrollFade.Left style={{ width: FADE_WIDTH }} active />
              </div>
            </div>
            <div
              className={classNames(styles.navigationButton, styles.right, {
                [styles.visible]: isMouseOver && !hasScrolledEnd,
              })}
            >
              <div className={styles.fade}>
                <HorizontalScrollFade.Right style={{ width: FADE_WIDTH }} active />
              </div>
              <Tooltip.New content={t('Move Right')}>
                <IconButton.New
                  onClick={() => onNavigateRight(scrollProps)}
                  appearance='secondary'
                  size='M'
                >
                  <NavigationRightIcon />
                </IconButton.New>
              </Tooltip.New>
            </div>
          </>
        )
      }}
    >
      {children}
    </HorizontalScroll>
  )
}
